/**
 * Subscription Service
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';
import type {
  Module,
  TenantSubscription,
  SmsUsage,
  PriceCalculation,
  CreateSubscriptionDto,
  UpdateSubscriptionDto,
  UpdateModulePriceDto,
  BillingReport,
} from '../types';

export const subscriptionService = {
  /**
   * Get all available modules
   */
  async getModules(): Promise<Module[]> {
    const response = await apiClient.get<Module[]>(API_ENDPOINTS.MODULES);
    return response.data;
  },

  /**
   * Get subscription for a tenant
   */
  async getByTenantId(tenantId: string): Promise<TenantSubscription> {
    const response = await apiClient.get<TenantSubscription>(
      `${API_ENDPOINTS.SUBSCRIPTIONS}/${tenantId}`
    );
    return response.data;
  },

  /**
   * Get all subscriptions (for billing overview)
   */
  async getAll(): Promise<TenantSubscription[]> {
    const response = await apiClient.get<TenantSubscription[]>(API_ENDPOINTS.SUBSCRIPTIONS);
    return response.data;
  },

  /**
   * Create a subscription for a tenant
   */
  async create(tenantId: string, dto: CreateSubscriptionDto): Promise<TenantSubscription> {
    const response = await apiClient.post<TenantSubscription>(
      `${API_ENDPOINTS.SUBSCRIPTIONS}/${tenantId}`,
      dto
    );
    return response.data;
  },

  /**
   * Update a subscription
   */
  async update(
    tenantId: string,
    dto: UpdateSubscriptionDto
  ): Promise<TenantSubscription> {
    const response = await apiClient.patch<TenantSubscription>(
      `${API_ENDPOINTS.SUBSCRIPTIONS}/${tenantId}`,
      dto
    );
    return response.data;
  },

  /**
   * Activate a module for a tenant
   */
  async activateModule(tenantId: string, moduleKey: string): Promise<void> {
    await apiClient.post(
      `${API_ENDPOINTS.SUBSCRIPTIONS}/${tenantId}/modules/${moduleKey}/activate`
    );
  },

  /**
   * Deactivate a module for a tenant
   */
  async deactivateModule(tenantId: string, moduleKey: string): Promise<void> {
    await apiClient.post(
      `${API_ENDPOINTS.SUBSCRIPTIONS}/${tenantId}/modules/${moduleKey}/deactivate`
    );
  },

  /**
   * Get SMS usage for a tenant
   */
  async getSmsUsage(tenantId: string, month?: string): Promise<SmsUsage> {
    const params = month ? { month } : {};
    const response = await apiClient.get<SmsUsage>(
      `${API_ENDPOINTS.SUBSCRIPTIONS}/${tenantId}/sms-usage`,
      { params }
    );
    return response.data;
  },

  /**
   * Calculate price for a tenant
   */
  async calculatePrice(tenantId: string): Promise<PriceCalculation> {
    const response = await apiClient.get<PriceCalculation>(
      `${API_ENDPOINTS.SUBSCRIPTIONS}/${tenantId}/price`
    );
    return response.data;
  },

  /**
   * Update module price override for a tenant
   */
  async updateModulePrice(
    tenantId: string,
    moduleKey: string,
    dto: UpdateModulePriceDto
  ): Promise<void> {
    await apiClient.patch(
      `${API_ENDPOINTS.SUBSCRIPTIONS}/${tenantId}/modules/${moduleKey}/price`,
      dto
    );
  },

  /**
   * Get billing report for a period
   */
  async getBillingReport(period?: string): Promise<BillingReport> {
    const params = period ? { period } : {};
    const response = await apiClient.get<BillingReport>(
      `${API_ENDPOINTS.SUBSCRIPTIONS}/billing/report`,
      { params }
    );
    return response.data;
  },

  /**
   * Update a module's base price (global)
   */
  async updateModule(moduleKey: string, dto: { basePrice?: number; isActive?: boolean }): Promise<Module> {
    const response = await apiClient.patch<Module>(
      `${API_ENDPOINTS.MODULES}/${moduleKey}`,
      dto
    );
    return response.data;
  },
};
